import * as React from 'react'
import { useInRouterContext } from 'react-router'

import { isMobile } from '../../../app/shared/isMobile'
import { DesktopSelect, DesktopSelectProps } from './DesktopSelect'
import { NativeSelect, NativeSelectInRouterContext } from './NativeSelect'
import { SelectButton } from './SelectButton'
import { SelectHandle, SelectItemGroup } from './types'
import { SelectItem } from './types'
import { findSelectedValue, isSelectItem, processItem } from './utils'

const isMobileDevice = isMobile()

export interface SelectProps<ValueType, ItemType, Name> extends DesktopSelectProps<ValueType, ItemType, Name> {
  /**
   * Whether or not to show a native select when clicking the button.
   * When rendering native select, you must provide `items`, `value` and `onChange`
   *
   * @default false
   */
  nativeSelect?: boolean | 'mobile'
}

function SelectComponent<ValueType, ItemType = ValueType, Name = never>(
  {
    name,
    label,
    value: providedValue,
    defaultValue: providedDefaultValue,
    onChange,
    items: providedItems,
    trigger,
    gutter,
    orientation,
    triggerContent,
    isInteractive = true,
    onInteractionChange,
    nativeSelect = false,
    comboBox = 10,
    onSearch,
    'data-testid': dataTestid,
    listProps,
    virtualized,
    ...props
  }: React.PropsWithChildren<SelectProps<ValueType, ItemType, Name>>,
  ref: React.ForwardedRef<SelectHandle>
) {
  // Used by native select to handle navigation for link items
  const isInRouterContext = useInRouterContext()
  const renderNativeSelect = nativeSelect === true || (nativeSelect === 'mobile' && isMobileDevice)
  // Let users either provide the selected item or its value. Items can also be grouped so we need to find the correct value
  const { value, defaultValue, items } = React.useMemo(() => {
    const items = (providedItems as Array<SelectItem<ValueType> | SelectItemGroup<ValueType>>)?.flatMap(processItem)

    // Fix for "Maximum update depth exceeded" https://github.com/finvizhq/Finviz-Website/pull/3706
    // Since the value is an object, and often it comes from something like Array.map -> Array.find, it's always a new reference and it triggers
    // https://github.com/ariakit/ariakit/blob/main/packages/ariakit-react-core/src/select/select.tsx#L162
    const value = findSelectedValue(items, providedValue) ?? providedValue
    const defaultValue = findSelectedValue(items, providedDefaultValue) ?? providedDefaultValue
    return {
      // Ignore for selects with option children
      value: !props.children && isSelectItem(value) ? value.value : value,
      defaultValue: !props.children && isSelectItem(defaultValue) ? defaultValue.value : defaultValue,
      items,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(providedValue),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(providedDefaultValue),
    providedItems,
  ])

  // Extracted trigger to a variable because it is used in every branch
  const selectTrigger = trigger ?? (
    <SelectButton
      {...props}
      // Bit of a workaround to provide the correct text to render for native select or when the select isn’t interactive to save resources
      valueOverride={
        !isInteractive || (nativeSelect !== false && items?.length) ? (value as SelectItem<ValueType>) : undefined
      }
      data-testid={renderNativeSelect ? undefined : dataTestid}
    >
      {triggerContent}
    </SelectButton>
  )

  // Save some resources by rendering the trigger only
  if (!isInteractive) {
    return selectTrigger
  }

  if (renderNativeSelect) {
    const NativeSelectComponent = isInRouterContext ? NativeSelectInRouterContext : NativeSelect
    const selectedValue = (isSelectItem(value) ? value.value : value) as string | number

    return (
      <div className="relative w-full">
        {selectTrigger}
        <NativeSelectComponent
          /**
           * The key attribute fixes error on iOS Safari, where the placeholder option wouldn’t get
           * hidden in some cases
           */
          key={selectedValue}
          items={items ?? []}
          onChange={(item) => onChange?.(item as any, name!)}
          value={selectedValue}
          placeholder={props.placeholder}
          data-testid={dataTestid}
        />
      </div>
    )
  }

  return (
    <DesktopSelect
      {...props}
      ref={ref}
      name={name}
      label={label}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange as any}
      items={items as any}
      trigger={selectTrigger}
      comboBox={comboBox}
      onSearch={onSearch}
      onInteractionChange={onInteractionChange}
      data-testid={dataTestid}
      gutter={gutter}
      orientation={orientation}
      listProps={listProps}
      virtualized={virtualized}
    />
  )
}

export const Select = React.forwardRef(SelectComponent) as <ValueType, ItemType = ValueType, Name = never>(
  props: React.PropsWithChildren<SelectProps<ValueType, ItemType, Name>> & {
    ref?: React.ForwardedRef<SelectHandle>
  }
) => ReturnType<typeof SelectComponent>
